import React, { useState, useEffect } from 'react';
import { Modal, Button, Tabs, Tab, Row, Col, Card } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import config from '../config';

const VenuePreviewModal = ({ show, onHide, venueId }) => {
    const { auth } = useAuth();
    const [venue, setVenue] = useState(null);
    const [menu, setMenu] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);
            try {
                // Fetch Venue Details (v2 includes features, blogs, gallery)
                const venueRes = await auth.fetch(`${config.API_BASE_URL}/api/v2/venues/${venueId}`);
                if (!venueRes.ok) throw new Error('Failed to fetch venue details');
                const venueData = await venueRes.json();
                setVenue(venueData);

                // Fetch Menu
                const menuRes = await auth.fetch(`${config.API_BASE_URL}/api/menus/venue/${venueId}`);
                if (menuRes.ok) {
                    const menuData = await menuRes.json();
                    setMenu(menuData);
                }
            } catch (err) {
                console.error('Error fetching preview data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (show && venueId) {
            fetchData();
        }
    }, [show, venueId, auth]);

    const getImageUrl = (path) => {
        if (!path) return '/assets/images/placeholder.png';
        if (path.startsWith('http')) return path;
        return `${config.API_BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    if (!show) return null;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered scrollable className="venue-preview-modal">
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <Icon icon="mdi:eye" className="text-primary-600" />
                    Venue Preview
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="p-0 bg-light">
                {loading ? (
                    <div className="d-flex justify-content-center align-items-center p-5" style={{ minHeight: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                ) : error ? (
                    <div className="text-center text-danger p-5">
                        <Icon icon="mdi:alert-circle-outline" className="text-4xl mb-2" />
                        <p>{error}</p>
                    </div>
                ) : venue ? (
                    <div>
                        {/* Hero Section */}
                        <div className="position-relative" style={{ height: '300px' }}>
                            <img 
                                src={getImageUrl(venue.thumbnail || (venue.gallery && venue.gallery[0]?.image_url))} 
                                alt={venue.title}
                                className="w-100 h-100 object-fit-cover"
                            />
                            <div className="position-absolute bottom-0 start-0 w-100 p-4 bg-gradient-to-t from-black/80 to-transparent text-white">
                                <div className="container">
                                    <div className="d-flex align-items-end gap-3">
                                        <img 
                                            src={getImageUrl(venue.logo)} 
                                            alt="Logo"
                                            className="w-80-px h-80-px rounded-circle border border-2 border-white bg-white object-fit-cover"
                                        />
                                        <div className="mb-1">
                                            <h2 className="h3 mb-1 fw-bold text-white text-shadow-sm">{venue.title}</h2>
                                            <p className="mb-0 opacity-90 text-shadow-sm">
                                                <Icon icon="mdi:map-marker" className="me-1" />
                                                {venue.city}, {venue.country}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Content */}
                        <div className="container py-4">
                            <Tabs defaultActiveKey="overview" className="mb-4 custom-tabs">
                                <Tab eventKey="overview" title="Overview">
                                    <Row className="g-4">
                                        <Col lg={8}>
                                            <Card className="border-0 shadow-sm mb-4">
                                                <Card.Body>
                                                    <h5 className="mb-3 fw-bold">About</h5>
                                                    <p className="text-secondary mb-0" style={{ whiteSpace: 'pre-line' }}>{venue.description}</p>
                                                </Card.Body>
                                            </Card>

                                            {/* Features */}
                                            {venue.features && venue.features.length > 0 && (
                                                <Card className="border-0 shadow-sm mb-4">
                                                    <Card.Body>
                                                        <h5 className="mb-3 fw-bold">Features & Amenities</h5>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {venue.features.map(feature => (
                                                                <div key={feature.id} className="d-flex align-items-center gap-2 px-3 py-2 bg-light rounded-pill border">
                                                                    {feature.icon && <Icon icon={feature.icon} className="text-primary-600" />}
                                                                    <span className="fw-medium">{feature.name}</span>
                                                                    {feature.price > 0 && <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded-pill">{feature.price} AED</span>}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </Card.Body>
                                                </Card>
                                            )}

                                            {/* Gallery Preview */}
                                            {venue.gallery && venue.gallery.length > 0 && (
                                                <Card className="border-0 shadow-sm">
                                                    <Card.Body>
                                                        <h5 className="mb-3 fw-bold">Gallery</h5>
                                                        <Row className="g-2">
                                                            {venue.gallery.slice(0, 6).map((img, idx) => (
                                                                <Col xs={4} md={3} key={img.id || idx}>
                                                                    <img 
                                                                        src={getImageUrl(img.image_url)} 
                                                                        alt="" 
                                                                        className="w-100 ratio-1x1 object-fit-cover rounded cursor-pointer hover-zoom"
                                                                        style={{ aspectRatio: '1/1' }}
                                                                    />
                                                                </Col>
                                                            ))}
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                            )}
                                        </Col>
                                        
                                        <Col lg={4}>
                                            <Card className="border-0 shadow-sm mb-4">
                                                <Card.Body>
                                                    <h6 className="fw-bold mb-3">Contact Information</h6>
                                                    <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
                                                        <li className="d-flex align-items-center gap-2">
                                                            <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                <Icon icon="mdi:email-outline" />
                                                            </div>
                                                            <span className="text-sm">{venue.email}</span>
                                                        </li>
                                                        <li className="d-flex align-items-center gap-2">
                                                            <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                <Icon icon="mdi:phone-outline" />
                                                            </div>
                                                            <span className="text-sm">{venue.phone}</span>
                                                        </li>
                                                        {venue.website && (
                                                            <li className="d-flex align-items-center gap-2">
                                                                <div className="w-32-px h-32-px rounded-circle bg-primary-50 text-primary-600 d-flex align-items-center justify-content-center">
                                                                    <Icon icon="mdi:web" />
                                                                </div>
                                                                <a href={venue.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary-600">{venue.website}</a>
                                                            </li>
                                                        )}
                                                    </ul>
                                                </Card.Body>
                                            </Card>

                                            <Card className="border-0 shadow-sm">
                                                <Card.Body>
                                                    <h6 className="fw-bold mb-3">Location</h6>
                                                    <p className="text-sm text-secondary mb-3">
                                                        {venue.street}, {venue.city}, {venue.state}, {venue.zip}
                                                    </p>
                                                    {/* Map Placeholder */}
                                                    <div className="bg-light rounded d-flex align-items-center justify-content-center text-secondary" style={{ height: '200px' }}>
                                                        <div className="text-center">
                                                            <Icon icon="mdi:map" className="text-4xl mb-2 opacity-50" />
                                                            <p className="mb-0 text-sm">Map View</p>
                                                        </div>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Tab>

                                <Tab eventKey="menu" title={`Menu (${menu?.items?.length || 0})`}>
                                    <div className="row g-4">
                                        <div className="col-lg-3">
                                            {/* Categories Sidebar */}
                                            <div className="card border-0 shadow-sm sticky-top" style={{ top: '20px', zIndex: 1 }}>
                                                <div className="card-body p-0">
                                                    <div className="list-group list-group-flush rounded">
                                                        {menu?.categories?.map(cat => (
                                                            <a href={`#cat-${cat.id}`} key={cat.id} className="list-group-item list-group-item-action border-0 px-3 py-2 fw-medium">
                                                                {cat.name}
                                                            </a>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-9">
                                            {menu?.categories?.map(cat => {
                                                const catItems = menu.items.filter(item => item.category_id === cat.id);
                                                if (catItems.length === 0) return null;
                                                
                                                return (
                                                    <div key={cat.id} id={`cat-${cat.id}`} className="mb-5">
                                                        <h5 className="fw-bold mb-3 border-bottom pb-2">{cat.name}</h5>
                                                        <div className="row g-3">
                                                            {catItems.map(item => (
                                                                <div className="col-md-6" key={item.id}>
                                                                    <div className="card h-100 border-0 shadow-sm hover-shadow transition-all">
                                                                        <div className="card-body d-flex gap-3">
                                                                            <div className="flex-grow-1">
                                                                                <div className="d-flex justify-content-between align-items-start mb-1">
                                                                                    <h6 className="fw-bold mb-0 text-truncate-2">{item.name}</h6>
                                                                                    <span className="fw-bold text-primary-600 ms-2 text-nowrap">{item.price} {item.currency}</span>
                                                                                </div>
                                                                                <p className="text-sm text-secondary mb-0 line-clamp-2">{item.description}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            {(!menu?.items || menu.items.length === 0) && (
                                                <div className="text-center py-5 text-secondary">
                                                    <Icon icon="mdi:food-off" className="text-4xl mb-2" />
                                                    <p>No menu items available</p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Tab>

                                <Tab eventKey="blogs" title={`Blogs (${venue.blogs?.length || 0})`}>
                                    <Row className="g-4">
                                        {venue.blogs?.map(blog => (
                                            <Col md={6} lg={4} key={blog.id}>
                                                <Card className="h-100 border-0 shadow-sm">
                                                    <div className="ratio ratio-16x9">
                                                        <img 
                                                            src={getImageUrl(blog.image_url)} 
                                                            className="card-img-top object-fit-cover" 
                                                            alt={blog.title} 
                                                        />
                                                    </div>
                                                    <Card.Body>
                                                        <div className="text-xs text-secondary mb-2">
                                                            {new Date(blog.created_at).toLocaleDateString()}
                                                        </div>
                                                        <h6 className="card-title fw-bold line-clamp-2">{blog.title}</h6>
                                                        <p className="card-text text-sm text-secondary line-clamp-3">
                                                            {blog.content?.replace(/<[^>]*>?/gm, '')}
                                                        </p>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                        {(!venue.blogs || venue.blogs.length === 0) && (
                                            <Col xs={12}>
                                                <div className="text-center py-5 text-secondary">
                                                    <Icon icon="mdi:post-outline" className="text-4xl mb-2" />
                                                    <p>No blog posts available</p>
                                                </div>
                                            </Col>
                                        )}
                                    </Row>
                                </Tab>
                            </Tabs>
                        </div>
                    </div>
                ) : null}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>Close Preview</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default VenuePreviewModal;